import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Pencil, X } from "lucide-react";
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatApiErrorDetail } from "@/lib/api";
import { formatPatientDisplayUhid } from "@/lib/patientDisplay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANY_OPTIONS = [
  "Medtronic",
  "Abbott",
  "Edwards",
  "Meril",
  "SMT",
  "Microport",
  "Allegra",
  "Boston Scientific",
  "Corcym",
  "Others",
];
const FREE_TEXT_SIZE_TYPES = ["Boston Scientific", "Corcym", "Others", "NA/Others"];
const MEDTRONIC_VALVE_TYPES = ["Evolut Pro+", "Evolut Fx", "Evolut Fx+"];
const MEDTRONIC_VALVE_SIZES = ["23", "26", "29", "34"];
const VALVE_SIZES_BY_TYPE = {
  Meril: ["20", "21.5", "23", "24.5", "26", "27.5", "29", "31.5", "34"],
  Edwards: ["20", "23", "26", "29"],
  Abbott: ["23", "25", "27", "29", "35"],
  SMT: ["22", "26", "30"],
  Microport: ["21", "24", "27", "30"],
  Allegra: ["21", "23", "27"],
};

const GENDER_OPTIONS = ["Male", "Female", "Other"];

const getSelectedCompany = (category, valveType) => {
  if (category === "Medtronic") return "Medtronic";
  if (category !== "Competition") return "";
  if (valveType === "NA/Others") return "Others";
  return valveType || "";
};

const applyCompanyToForm = (prev, company) => {
  if (company === "Medtronic") {
    return { ...prev, valve_manufacturer_category: "Medtronic", valve_type: "", valve_size: "" };
  }
  return { ...prev, valve_manufacturer_category: "Competition", valve_type: company, valve_size: "" };
};

const getValveSizeOptions = (valveType) => {
  if (MEDTRONIC_VALVE_TYPES.includes(valveType)) return MEDTRONIC_VALVE_SIZES;
  if (FREE_TEXT_SIZE_TYPES.includes(valveType)) return null;
  return VALVE_SIZES_BY_TYPE[valveType] ?? [];
};

const normalizeGender = (gender) => {
  if (!gender) return "Male";
  const g = String(gender);
  if (g === "M") return "Male";
  if (g === "F") return "Female";
  return g;
};

const buildForm = (patient = {}) => ({
  name: patient.name ?? "",
  age: patient.age != null ? String(patient.age) : "",
  gender: normalizeGender(patient.gender),
  date_of_birth: patient.date_of_birth ?? "",
  contact: patient.contact ?? "",
  email: patient.email ?? "",
  blood_group: patient.blood_group ?? "",
  referring_doctor: patient.referring_doctor ?? "",
  institution_name: patient.institution_name ?? "",
  status: patient.status === "completed" ? "completed" : "pending",
  valve_manufacturer_category: patient.valve_manufacturer_category ?? "",
  valve_type: patient.valve_type ?? "",
  valve_size: patient.valve_size != null ? String(patient.valve_size) : "",
});

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   patient?: object | null,
 *   uhid?: string,
 *   onSaved?: (patient: object) => void,
 * }} props
 */
const EditPatientDialog = ({ open, onOpenChange, patient, uhid, onSaved }) => {
  const patientUhid = patient?.uhid ?? uhid;
  const [form, setForm] = useState(buildForm());
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displayUhid, setDisplayUhid] = useState(patientUhid);

  useEffect(() => {
    if (!open || !patientUhid) return undefined;

    let cancelled = false;
    setLoading(true);
    setTagInput("");

    const hydrate = async () => {
      try {
        const res = await axios.get(`${API}/patients/${encodeURIComponent(patientUhid)}`);
        if (cancelled) return;
        const next = res.data?.patient ?? res.data;
        setForm(buildForm(next));
        setTags(Array.isArray(next?.tags) ? [...next.tags] : []);
        setDisplayUhid(formatPatientDisplayUhid(next) || patientUhid);
      } catch {
        if (cancelled) return;
        if (patient) {
          setForm(buildForm(patient));
          setTags(Array.isArray(patient.tags) ? [...patient.tags] : []);
          setDisplayUhid(formatPatientDisplayUhid(patient) || patientUhid);
        } else {
          toast.error("Failed to load patient");
          onOpenChange(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    hydrate();
    return () => { cancelled = true; };
  }, [open, patient, patientUhid, onOpenChange]);

  const selectedCompany = getSelectedCompany(form.valve_manufacturer_category, form.valve_type);
  const isMedtronicCompany = form.valve_manufacturer_category === "Medtronic";
  const sizeOptions = getValveSizeOptions(form.valve_type);
  const isNaOthers = FREE_TEXT_SIZE_TYPES.includes(form.valve_type);
  const isCompleted = form.status === "completed";

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) return;
    if (tags.includes(trimmed)) {
      toast.error("Tag already exists");
      return;
    }
    setTags((prev) => [...prev, trimmed]);
    setTagInput("");
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tag) => {
    setTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleStatusChange = (value) => {
    setForm((prev) => ({
      ...prev,
      status: value,
      ...(value === "pending"
        ? { valve_manufacturer_category: "", valve_type: "", valve_size: "" }
        : {}),
    }));
  };

  const handleSave = async () => {
    if (!patientUhid) return;
    if (!form.age) {
      toast.error("Age is required");
      return;
    }
    if (isCompleted) {
      const { valve_manufacturer_category, valve_type, valve_size } = form;
      if (!valve_manufacturer_category || !valve_type || !valve_size?.trim()) {
        toast.error("Select company, valve type, and size for completed status");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        age: parseInt(form.age, 10),
        gender: form.gender || null,
        date_of_birth: form.date_of_birth || null,
        contact: form.contact || null,
        email: form.email || null,
        blood_group: form.blood_group || null,
        referring_doctor: form.referring_doctor || null,
        institution_name: form.institution_name || null,
        tags,
        status: form.status,
        valve_manufacturer_category: isCompleted ? form.valve_manufacturer_category : null,
        valve_type: isCompleted ? form.valve_type : null,
        valve_size: isCompleted ? form.valve_size.trim() : null,
      };
      const res = await axios.put(`${API}/patients/${encodeURIComponent(patientUhid)}`, payload);
      toast.success("Patient updated");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update patient");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="p-0 w-full sm:max-w-[720px] flex flex-col h-full"
        data-testid="edit-patient-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0 space-y-1 text-left">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Pencil className="w-4 h-4" /> Edit Patient
          </SheetTitle>
          <SheetDescription>
            Update patient details for {displayUhid || patientUhid}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Full Name</Label>
                  <Input
                    data-testid="edit-patient-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Patient name"
                  />
                </div>
                {/* <div className="space-y-1.5">
                  <Label className="text-xs">Contact</Label>
                  <Input
                    data-testid="edit-patient-contact"
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    placeholder="+91-..."
                  />
                </div> */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Doctor</Label>
                  <Input
                    data-testid="edit-patient-doctor"
                    value={form.referring_doctor}
                    onChange={(e) => setForm({ ...form, referring_doctor: e.target.value })}
                    placeholder="Dr. ..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Age *</Label>
                  <Input
                    data-testid="edit-patient-age"
                    type="number"
                    value={form.age}
                    onChange={(e) => setForm({ ...form, age: e.target.value })}
                    placeholder="45"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Gender</Label>
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger data-testid="edit-patient-gender"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map((g) => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Hospital</Label>
                  <Input
                    data-testid="edit-patient-hospital"
                    value={form.institution_name}
                    onChange={(e) => setForm({ ...form, institution_name: e.target.value })}
                    placeholder="Hospital name"
                  />
                </div>
              </div>

              {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Blood Group</Label>
                  <Select
                    value={form.blood_group || undefined}
                    onValueChange={(v) => setForm({ ...form, blood_group: v })}
                  >
                    <SelectTrigger data-testid="edit-patient-blood"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((bg) => (
                        <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Email</Label>
                  <Input
                    data-testid="edit-patient-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Doctor</Label>
                  <Input
                    data-testid="edit-patient-doctor"
                    value={form.referring_doctor}
                    onChange={(e) => setForm({ ...form, referring_doctor: e.target.value })}
                    placeholder="Dr. ..."
                  />
                </div>
              </div> */}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Status</Label>
                  <Select value={form.status} onValueChange={handleStatusChange}>
                    <SelectTrigger data-testid="edit-patient-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="edit-patient-tag-input"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder="Add a tag"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddTag}
                      data-testid="edit-patient-add-tag"
                      className="shrink-0"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              {isCompleted && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Company *</Label>
                    <Select
                      value={selectedCompany || undefined}
                      onValueChange={(value) => setForm((prev) => applyCompanyToForm(prev, value))}
                    >
                      <SelectTrigger data-testid="edit-patient-company">
                        <SelectValue placeholder="Select company" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isMedtronicCompany && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Valve Type *</Label>
                      <Select
                        value={form.valve_type || undefined}
                        onValueChange={(value) => setForm((prev) => ({
                          ...prev,
                          valve_type: value,
                          valve_size: "",
                        }))}
                      >
                        <SelectTrigger data-testid="edit-patient-valve-type">
                          <SelectValue placeholder="Select valve type" />
                        </SelectTrigger>
                        <SelectContent>
                          {MEDTRONIC_VALVE_TYPES.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Size *</Label>
                    {isNaOthers ? (
                      <Input
                        data-testid="edit-patient-valve-size"
                        value={form.valve_size}
                        onChange={(e) => setForm((prev) => ({ ...prev, valve_size: e.target.value }))}
                        placeholder="Enter size"
                      />
                    ) : (
                      <Select
                        value={form.valve_size || undefined}
                        onValueChange={(value) => setForm((prev) => ({ ...prev, valve_size: value }))}
                        disabled={!form.valve_type || !sizeOptions?.length}
                      >
                        <SelectTrigger data-testid="edit-patient-valve-size">
                          <SelectValue placeholder={sizeOptions?.length ? "Select size" : "Select company first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {sizeOptions?.map((size) => (
                            <SelectItem key={size} value={size}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              )}

              {tags.length > 0 && (
                <div className="space-y-1.5" data-testid="edit-patient-tags">
                  <Label className="text-xs">Added Tags</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="text-xs font-normal gap-1 pr-1"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="rounded-full p-0.5 hover:bg-background/60"
                          aria-label={`Remove ${tag}`}
                          data-testid={`edit-remove-tag-${tag}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <SheetFooter className="px-5 py-4 border-t border-border shrink-0 flex-row justify-end gap-2 sm:space-x-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-edit-patient">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="save-edit-patient">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default EditPatientDialog;
